import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Chip, CircularProgress,
  Tabs, Tab, Alert, IconButton, Divider, Tooltip, RadioGroup, Radio, FormControlLabel,
  useTheme, useMediaQuery
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BlockIcon from '@mui/icons-material/Block';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import BadgeIcon from '@mui/icons-material/Badge';
import DatosPacienteDialog from '../components/DatosPacienteDialog';

const API = process.env.REACT_APP_API_URL || '';

const fieldSx = {
  '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'var(--c-primario)' } },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-primario)' },
};
const inputStyle = { style: { color: '#e0e0e0' } };
const labelStyle = { style: { color: 'var(--c-acento)' }, shrink: true };

const FICHA_VACIA = {
  peso: '', altura: '', grupo_sanguineo: '', factor_rh: '',
  alergias: '', medicacion_habitual: '', antecedentes_quirurgicos: '',
  enfermedades_cronicas: '', habito_tabaco: 'no', habito_alcohol: 'no', otros: '',
};

const EV_VACIA = {
  texto: '', diagnostico: '',
};

const MOTIVOS_FUERA_TURNO = [
  { value: 'olvido_registro',    label: 'Olvidé registrar algo de una atención anterior' },
  { value: 'contacto_otro_medio', label: 'El paciente se comunicó por otro medio' },
  { value: 'previo_a_atencion',  label: 'Registro previo a una atención' },
  { value: 'otro',               label: 'Otro' },
];
const MOTIVO_FUERA_TURNO_LABEL = Object.fromEntries(MOTIVOS_FUERA_TURNO.map(m => [m.value, m.label]));

// ── Componente TagInput ──────────────────────────────────────────────────────
function TagInput({ label, tags, onChange, max = 50, placeholder }) {
  const [input, setInput] = useState('');

  function agregar() {
    const val = input.trim();
    if (!val || tags.includes(val) || tags.length >= max) return;
    onChange([...tags, val]);
    setInput('');
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', mb: 0.5 }}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          size="small" value={input} placeholder={placeholder || 'Escribí y presioná Enter'}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); agregar(); } }}
          inputProps={inputStyle} sx={{ flex: 1, ...fieldSx }}
        />
        <Button variant="outlined" onClick={agregar} size="small"
          sx={{ color: 'var(--c-acento)', borderColor: 'var(--c-primario)', minWidth: 36 }}>
          +
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {tags.map((t, i) => (
          <Chip key={i} label={t} size="small"
            onDelete={() => onChange(tags.filter((_, j) => j !== i))}
            sx={{ bgcolor: 'var(--c-primario)', color: '#e0e0e0', fontSize: 12 }} />
        ))}
      </Box>
    </Box>
  );
}

// ── Tarjeta de evolución ─────────────────────────────────────────────────────
function EvolucionCard({ ev, onAnular, rol }) {
  const [expandida, setExpandida] = useState(false);
  const [confirmAnular, setConfirmAnular] = useState(false);
  const [anulando, setAnulando] = useState(false);

  const fecha = new Date(ev.created_at);
  const fechaStr = fecha.toLocaleDateString('es-AR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const horaStr = fecha.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  const preview = ev.texto?.replace(/<[^>]*>/g, '').substring(0, 120) + (ev.texto?.length > 120 ? '...' : '');

  const medicamentos = ev.medicamentos ? (typeof ev.medicamentos === 'string' ? JSON.parse(ev.medicamentos) : ev.medicamentos) : [];
  const estudios = ev.estudios ? (typeof ev.estudios === 'string' ? JSON.parse(ev.estudios) : ev.estudios) : [];

  async function confirmarAnular() {
    setAnulando(true);
    await onAnular(ev.id);
    setAnulando(false);
    setConfirmAnular(false);
  }

  return (
    <Box sx={{
      bgcolor: 'var(--c-superficie)', borderRadius: 2,
      border: ev.anulada ? '1px solid #555' : '1px solid var(--c-primario)',
      opacity: ev.anulada ? 0.6 : 1, mb: 1.5
    }}>
      {/* Header colapsado */}
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, cursor: 'pointer', gap: 1 }}
        onClick={() => setExpandida(!expandida)}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ev.anulada ? '#555' : '#1565c0', flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', fontWeight: 600, mr: 1, flexShrink: 0 }}>
          EVOLUCIÓN
        </Typography>
        {!!ev.anulada && <Chip label="ANULADA" size="small" sx={{ bgcolor: '#b71c1c', color: '#fff', fontSize: 10, height: 18 }} />}
        {!!ev.fuera_de_turno && (
          <Chip
            label={`Fuera de turno${ev.motivo_fuera_turno ? ` · ${MOTIVO_FUERA_TURNO_LABEL[ev.motivo_fuera_turno] || ev.motivo_fuera_turno}` : ''}`}
            size="small" sx={{ bgcolor: 'rgba(255,183,77,0.18)', color: '#ffb74d', border: '1px solid rgba(255,183,77,0.4)', fontSize: 10, height: 18 }} />
        )}
        <Typography sx={{ fontSize: 13, color: '#b0b0b0', mr: 1, flexShrink: 0 }}>{fechaStr}</Typography>
        {!expandida && (
          <Typography sx={{ fontSize: 13, color: '#e0e0e0', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {preview}
          </Typography>
        )}
        <IconButton size="small" sx={{ color: 'var(--c-acento)', ml: 'auto' }}>
          {expandida ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Contenido expandido */}
      {expandida && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2 }} />

          {/* Texto */}
          <Typography sx={{ color: '#e0e0e0', fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.7, mb: 2 }}>
            {ev.texto}
          </Typography>

          {!!ev.fuera_de_turno && ev.motivo_fuera_turno_detalle && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, color: '#ffb74d', fontWeight: 600, mb: 0.5 }}>Aclaración (fuera de turno)</Typography>
              <Typography sx={{ fontSize: 14, color: '#e0e0e0' }}>{ev.motivo_fuera_turno_detalle}</Typography>
            </Box>
          )}

          {/* Diagnóstico */}
          {ev.diagnostico && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', fontWeight: 600, mb: 0.5 }}>Diagnóstico</Typography>
              <Typography sx={{ fontSize: 14, color: '#e0e0e0' }}>{ev.diagnostico}</Typography>
            </Box>
          )}

          {/* Medicamentos */}
          {medicamentos.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', fontWeight: 600, mb: 0.5 }}>Medicamentos indicados</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {medicamentos.map((m, i) => (
                  <Chip key={i} label={m} size="small" sx={{ bgcolor: '#1b5e20', color: '#c8e6c9', fontSize: 12 }} />
                ))}
              </Box>
            </Box>
          )}

          {/* Estudios */}
          {estudios.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', fontWeight: 600, mb: 0.5 }}>Estudios solicitados</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {estudios.map((e, i) => (
                  <Chip key={i} label={e} size="small" sx={{ bgcolor: '#0d47a1', color: '#bbdefb', fontSize: 12 }} />
                ))}
              </Box>
            </Box>
          )}

          {/* Bloque auditoría */}
          <Box sx={{ bgcolor: 'rgba(46,125,50,0.1)', border: '1px solid #2e7d32', borderRadius: 1, p: 1.5, mt: 2 }}>
            <Typography sx={{ fontSize: 11, color: '#81c784' }}>
              Cargado por: <strong>{ev.cargado_por_nombre}</strong>
              {ev.cargado_por_matricula && ` · Mat. ${ev.cargado_por_matricula}`}
              {ev.cargado_por_especialidad && ` · ${ev.cargado_por_especialidad}`}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#81c784' }}>
              {fechaStr} a las {horaStr}
            </Typography>
            {!!ev.anulada && (
              <Typography sx={{ fontSize: 11, color: '#ef9a9a', mt: 0.5 }}>
                Anulada por: {ev.anulada_por}
              </Typography>
            )}
          </Box>

          {/* Acciones */}
          {!ev.anulada && ['admin', 'medico'].includes(rol) && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button size="small" startIcon={<BlockIcon />} onClick={() => setConfirmAnular(true)}
                sx={{ color: '#ef9a9a', borderColor: '#ef9a9a', fontSize: 12 }} variant="outlined">
                Anular
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Dialog confirmar anulación */}
      <Dialog open={confirmAnular} onClose={() => setConfirmAnular(false)}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: '#ef9a9a' }}>Anular evolución</DialogTitle>
        <DialogContent>
          <Typography>Esta acción no se puede deshacer. La evolución quedará visible pero marcada como anulada.</Typography>
          <Typography sx={{ color: '#ffb74d', fontWeight: 600, mt: 1 }}>¿Confirmás la anulación?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAnular(false)} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button onClick={confirmarAnular} variant="contained" disabled={anulando}
            sx={{ bgcolor: '#b71c1c', '&:hover': { bgcolor: '#7f0000' } }}>
            {anulando ? 'Anulando...' : 'Sí, anular'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Página principal ─────────────────────────────────────────────────────────
export default function Historial() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rol = sessionStorage.getItem('rol');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [paciente, setPaciente] = useState(null);
  const [ficha, setFicha] = useState(null);
  const [evoluciones, setEvoluciones] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [descargandoEvId, setDescargandoEvId] = useState(null);

  // Ficha básica
  const [fichaOpen, setFichaOpen] = useState(false);
  const [verDatosPaciente, setVerDatosPaciente] = useState(false);
  const [fichaForm, setFichaForm] = useState(FICHA_VACIA);
  const [guardandoFicha, setGuardandoFicha] = useState(false);
  const [fichaExito, setFichaExito] = useState(false);

  // Nueva evolución
  const [evOpen, setEvOpen] = useState(false);
  const [evForm, setEvForm] = useState(EV_VACIA);
  const [evMedicamentos, setEvMedicamentos] = useState([]);
  const [evEstudios, setEvEstudios] = useState([]);
  const [evPalabrasClave, setEvPalabrasClave] = useState([]);
  const [evTurnoId, setEvTurnoId] = useState(null);
  const [confirmGuardarEv, setConfirmGuardarEv] = useState(false);
  const [guardandoEv, setGuardandoEv] = useState(false);
  const [evError, setEvError] = useState('');
  const [verificandoTurno, setVerificandoTurno] = useState(false);
  const [turnoCompatible, setTurnoCompatible] = useState(null);
  const [showTurnoCompatible, setShowTurnoCompatible] = useState(false);
  const [showFueraDeTurno, setShowFueraDeTurno] = useState(false);
  const [motivoFueraTurno, setMotivoFueraTurno] = useState('');
  const [motivoFueraTurnoDetalle, setMotivoFueraTurnoDetalle] = useState('');
  const [avisoTurnoNoActualizado, setAvisoTurnoNoActualizado] = useState(false);

  // Archivos
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);
  const [archivoDesc, setArchivoDesc] = useState('');
  const [archivoError, setArchivoError] = useState('');
  const fileInputRef = useRef();

  async function descargarPDF() {
    setDescargandoPdf(true);
    try {
      const r = await axios.get(`${API}/api/pacientes/${id}/historial.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `historial-${id}.pdf`);
      document.body.appendChild(link);
      link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Error descargando PDF:', err); }
    finally { setDescargandoPdf(false); }
  }

  async function descargarEvolucionPDF(ev) {
    setDescargandoEvId(ev.id);
    try {
      const url = ev.turno_id
        ? `${API}/api/turnos/${ev.turno_id}/consulta.pdf`
        : `${API}/api/evoluciones/${ev.id}/pdf`;
      const r = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      const fecha = new Date(ev.created_at).toISOString().split('T')[0];
      link.setAttribute('download', `evolucion-${fecha}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) { console.error('Error descargando PDF evolucion:', err); }
    finally { setDescargandoEvId(null); }
  }

  useEffect(() => { cargarDatos(); }, [id]);

  useEffect(() => {
    if (searchParams.get('nueva') === '1') {
      const turno = searchParams.get('turno');
      setEvTurnoId(turno ? parseInt(turno) : null);
      setEvError('');
      setEvOpen(true);
    }
  }, [searchParams]);

  async function cargarDatos() {
    setLoading(true);
    try {
      const [pacRes, fichaRes, evRes, archRes] = await Promise.all([
        axios.get(`${API}/api/pacientes/${id}`).catch(() => ({ data: null })),
        axios.get(`${API}/api/pacientes/${id}/ficha`).catch(() => ({ data: null })),
        axios.get(`${API}/api/pacientes/${id}/evoluciones`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/pacientes/${id}/archivos`).catch(() => ({ data: [] })),
      ]);
      const pac = pacRes.data;
      setPaciente(pac);
      const f = fichaRes.data;
      setFicha(f);
      setFichaForm(f ? {
        peso: f.peso || '', altura: f.altura || '',
        grupo_sanguineo: f.grupo_sanguineo || '', factor_rh: f.factor_rh || '',
        alergias: f.alergias || '', medicacion_habitual: f.medicacion_habitual || '',
        antecedentes_quirurgicos: f.antecedentes_quirurgicos || '',
        enfermedades_cronicas: f.enfermedades_cronicas || '',
        habito_tabaco: f.habito_tabaco || 'no', habito_alcohol: f.habito_alcohol || 'no',
        otros: f.otros || '',
      } : FICHA_VACIA);
      setEvoluciones(evRes.data || []);
      setArchivos(archRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function guardarFicha() {
    setGuardandoFicha(true);
    try {
      await axios.put(`${API}/api/pacientes/${id}/ficha`, fichaForm);
      setFichaExito(true);
      setTimeout(() => setFichaExito(false), 3000);
      cargarDatos();
    } catch (err) { console.error(err); }
    finally { setGuardandoFicha(false); }
  }

  // Limpia todo el estado del modal "Nueva Evolución" tras un guardado exitoso (cualquiera
  // de los 3 caminos: vinculado a un turno ya conocido, turno compatible encontrado al
  // guardar, o fuera de turno).
  function limpiarFormularioEvolucion() {
    setEvOpen(false);
    setConfirmGuardarEv(false);
    setShowTurnoCompatible(false);
    setShowFueraDeTurno(false);
    setEvForm(EV_VACIA);
    setEvPalabrasClave([]);
    setEvMedicamentos([]);
    setEvEstudios([]);
    setEvTurnoId(null);
    setTurnoCompatible(null);
    setMotivoFueraTurno('');
    setMotivoFueraTurnoDetalle('');
  }

  async function guardarEvolucion() {
    if (!evForm.texto.trim()) { setEvError('El texto es obligatorio.'); return; }
    setEvError('');
    // Si ya se llegó con un turno específico (ej. desde el aviso de recepción, ?turno=X),
    // ese vínculo ya es explícito -- se guarda directo, sin preguntar nada, como siempre.
    if (evTurnoId) { setConfirmGuardarEv(true); return; }
    // Caso "Nueva Evolución" sin turno conocido: recién ahora, al guardar (no al abrir el
    // modal), se pregunta si hay un turno compatible con la hora actual.
    setVerificandoTurno(true);
    try {
      const r = await axios.get(`${API}/api/pacientes/${id}/turno-compatible`);
      if (r.data?.turno) { setTurnoCompatible(r.data.turno); setShowTurnoCompatible(true); }
      else { setShowFueraDeTurno(true); }
    } catch {
      // Si la verificación falla, no bloqueamos el guardado -- se trata como si no hubiera
      // turno compatible, así el médico igual puede guardar su evolución (fuera de turno).
      setShowFueraDeTurno(true);
    } finally { setVerificandoTurno(false); }
  }

  async function confirmarGuardarEvolucion() {
    setGuardandoEv(true);
    setEvError('');
    try {
      await axios.post(`${API}/api/pacientes/${id}/evoluciones`, {
        texto: evForm.texto,
        diagnostico: evForm.diagnostico || null,
        medicamentos: evMedicamentos,
        estudios: evEstudios,
        palabras_clave: evPalabrasClave.length ? evPalabrasClave : null,
        turno_id: evTurnoId || null,
      });
      limpiarFormularioEvolucion();
      cargarDatos();
    } catch (err) {
      setEvError(err.response?.data?.error || 'Error al guardar.');
      setConfirmGuardarEv(false);
    } finally { setGuardandoEv(false); }
  }

  // Caso C.2 del diseño: hay un turno compatible (±3h). "marcarAtendido" define si además
  // de guardar la evolución se marca el turno como atendido -- nunca es obligatorio.
  async function guardarConTurnoCompatible(marcarAtendido) {
    setGuardandoEv(true);
    setEvError('');
    try {
      const r = await axios.post(`${API}/api/pacientes/${id}/evoluciones/con-turno`, {
        texto: evForm.texto,
        diagnostico: evForm.diagnostico || null,
        medicamentos: evMedicamentos,
        estudios: evEstudios,
        palabras_clave: evPalabrasClave.length ? evPalabrasClave : null,
        turno_id: turnoCompatible.id,
        marcar_atendido: marcarAtendido,
      });
      const huboFalloParcial = marcarAtendido && !r.data?.turno_actualizado;
      limpiarFormularioEvolucion();
      if (huboFalloParcial) {
        // La evolución quedó guardada -- nunca se sugiere reintentar, porque reintentar
        // duplicaría la evolución. Solo se avisa que falta marcar el turno a mano.
        setAvisoTurnoNoActualizado(true);
        setTimeout(() => setAvisoTurnoNoActualizado(false), 8000);
      }
      cargarDatos();
    } catch (err) {
      setEvError(err.response?.data?.error || 'Error al guardar.');
    } finally { setGuardandoEv(false); }
  }

  // Caso C.3 del diseño: no hay turno compatible. "fuera_de_turno" solo se establece acá,
  // después de que el médico confirmó un motivo -- nunca antes.
  async function guardarFueraDeTurno() {
    if (!motivoFueraTurno) { setEvError('Elegí un motivo para guardar.'); return; }
    setGuardandoEv(true);
    setEvError('');
    try {
      await axios.post(`${API}/api/pacientes/${id}/evoluciones`, {
        texto: evForm.texto,
        diagnostico: evForm.diagnostico || null,
        medicamentos: evMedicamentos,
        estudios: evEstudios,
        palabras_clave: evPalabrasClave.length ? evPalabrasClave : null,
        turno_id: null,
        fuera_de_turno: true,
        motivo_fuera_turno: motivoFueraTurno,
        motivo_fuera_turno_detalle: motivoFueraTurno === 'otro' ? (motivoFueraTurnoDetalle || null) : null,
      });
      limpiarFormularioEvolucion();
      cargarDatos();
    } catch (err) {
      setEvError(err.response?.data?.error || 'Error al guardar.');
    } finally { setGuardandoEv(false); }
  }

  async function anularEvolucion(eid) {
    await axios.put(`${API}/api/pacientes/${id}/evoluciones/${eid}/anular`);
    cargarDatos();
  }

  async function subirArchivo(e) {
    const file = e.target.files[0];
    if (!file) return;
    setSubiendoArchivo(true);
    setArchivoError('');
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('descripcion', archivoDesc);
      await axios.post(`${API}/api/pacientes/${id}/archivos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setArchivoDesc('');
      cargarDatos();
    } catch (err) {
      setArchivoError(err.response?.data?.error || 'Error al subir el archivo');
    }
    finally { setSubiendoArchivo(false); e.target.value = ''; }
  }

  async function eliminarArchivo(aid) {
    if (!window.confirm('¿Eliminár este archivo?')) return;
    await axios.delete(`${API}/api/pacientes/${id}/archivos/${aid}`);
    cargarDatos();
  }

  function imc() {
    const p = parseFloat(fichaForm.peso), a = parseFloat(fichaForm.altura);
    if (!p || !a || a <= 0) return null;
    return (p / (a * a)).toFixed(1);
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress sx={{ color: 'var(--c-acento)' }} />
    </Box>
  );

  const nombrePaciente = paciente
    ? (paciente.apellido ? `${paciente.apellido}, ${paciente.nombre || ''}` : (paciente.nombre || '')).trim()
    : `Paciente #${id}`;

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
          sx={{ color: 'var(--c-acento)' }}>
          Volver
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
            Historia Clínica — {nombrePaciente}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#90a4ae' }}>
            {paciente?.dni && `DNI: ${paciente.dni}`}
            {paciente?.obra_social && ` · ${paciente.obra_social}`}
            {paciente?.numero_afiliado && ` Nº ${paciente.numero_afiliado}`}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<LocalHospitalIcon />}
          onClick={() => setFichaOpen(true)}
          sx={{ color: 'var(--c-acento)', borderColor: 'var(--c-primario)' }}>
          Ficha Básica
        </Button>
        <Button variant="outlined" startIcon={<BadgeIcon />}
          onClick={() => setVerDatosPaciente(true)}
          sx={{ color: '#90a4ae', borderColor: '#546e7a' }}>
          Ver datos del paciente
        </Button>
        {['admin', 'medico'].includes(rol) && (
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />}
            onClick={() => setPdfOpen(true)}
            sx={{ color: '#ef9a9a', borderColor: '#ef9a9a' }}>
            Descargar PDF
          </Button>
        )}
        {['admin', 'medico'].includes(rol) && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { setEvOpen(true); setEvError(''); }}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
            Nueva Evolución
          </Button>
        )}
      </Box>

      {/* ── Alerta alergias ── */}
      {ficha?.alergias && (
        <Alert severity="warning" sx={{ mb: 2, fontWeight: 600 }}>
          ⚠️ Alergias: {ficha.alergias}
        </Alert>
      )}

      {/* ── Aviso: evolución guardada pero no se pudo marcar el turno ── */}
      {avisoTurnoNoActualizado && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAvisoTurnoNoActualizado(false)}>
          La evolución se guardó correctamente, pero no pudimos marcar el turno como atendido. Marcalo manualmente desde Turnos.
        </Alert>
      )}

      {/* ── Tabs ── */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, '& .MuiTab-root': { color: '#b0b0b0' }, '& .Mui-selected': { color: 'var(--c-acento)' } }}>
        <Tab label={`Evoluciones (${evoluciones.length})`} />
        <Tab label={`Archivos (${archivos.length})`} />
      </Tabs>

      {/* ── Tab evoluciones ── */}
      {tab === 0 && (
        <Box>
          {evoluciones.length === 0 ? (
            <Typography sx={{ color: '#666', mt: 2 }}>No hay evoluciones registradas.</Typography>
          ) : (
            evoluciones.map(ev => (
              <EvolucionCard key={ev.id} ev={ev} onAnular={anularEvolucion} rol={rol} />
            ))
          )}
        </Box>
      )}

      {/* ── Tab archivos ── */}
      {tab === 1 && (
        <Box>
          {['admin', 'medico'].includes(rol) && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <TextField label="Descripción del archivo" value={archivoDesc}
                onChange={e => setArchivoDesc(e.target.value)}
                InputLabelProps={labelStyle} inputProps={inputStyle}
                sx={{ flex: 1, minWidth: 200, ...fieldSx }} />
              <input type="file" ref={fileInputRef} style={{ display: 'none' }}
                onChange={subirArchivo} accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt" />
              <Button variant="contained" startIcon={<AttachFileIcon />}
                onClick={() => fileInputRef.current.click()}
                disabled={subiendoArchivo}
                sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' }, height: 56 }}>
                {subiendoArchivo ? 'Subiendo...' : 'Subir archivo'}
              </Button>
            </Box>
          )}
          {archivoError && (
            <Alert severity="error" onClose={() => setArchivoError('')} sx={{ mb: 2, bgcolor: 'rgba(183,28,28,0.15)', color: '#ef9a9a' }}>
              {archivoError}
            </Alert>
          )}
          {archivos.length === 0 ? (
            <Typography sx={{ color: '#666' }}>No hay archivos adjuntos.</Typography>
          ) : (
            archivos.map(a => (
              <Box key={a.id} sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                bgcolor: 'var(--c-superficie)', p: 1.5, borderRadius: 2,
                border: '1px solid var(--c-primario)', mb: 1
              }}>
                <AttachFileIcon sx={{ color: 'var(--c-acento)', flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 14, color: '#e0e0e0', fontWeight: 600 }}>
                    {a.nombre_original}
                  </Typography>
                  {a.descripcion && (
                    <Typography sx={{ fontSize: 12, color: '#90a4ae' }}>{a.descripcion}</Typography>
                  )}
                  <Typography sx={{ fontSize: 11, color: '#666' }}>
                    {a.cargado_por} · {new Date(a.created_at).toLocaleDateString('es-AR')} · {(a.tamanio / 1024).toFixed(0)} KB
                  </Typography>
                </Box>
                <Tooltip title="Descargar">
                  <IconButton size="small" sx={{ color: 'var(--c-acento)' }}
                    href={`${API}/uploads/hc/${a.nombre_archivo}`} target="_blank" component="a">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                {['admin', 'medico'].includes(rol) && (
                  <Tooltip title="Eliminar">
                    <IconButton size="small" sx={{ color: '#ef9a9a' }} onClick={() => eliminarArchivo(a.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))
          )}
        </Box>
      )}

      {/* ── Modal selector PDF ── */}
      <Dialog open={pdfOpen} onClose={() => setPdfOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: 'var(--c-acento)', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PictureAsPdfIcon sx={{ color: '#ef9a9a' }} /> ¿Qué querés descargar?
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>

          {/* HC Completa */}
          <Box sx={{ p: 2, border: '1px solid var(--c-primario)', borderRadius: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Historia Clínica Completa</Typography>
              <Typography sx={{ fontSize: 12, color: '#90a4ae' }}>
                Ficha básica + todas las evoluciones ({evoluciones.length})
              </Typography>
            </Box>
            <Button variant="contained" size="small" startIcon={<PictureAsPdfIcon />}
              onClick={() => { descargarPDF(); setPdfOpen(false); }}
              disabled={descargandoPdf}
              sx={{ bgcolor: '#ef9a9a', color: '#000', fontWeight: 700, flexShrink: 0,
                '&:hover': { bgcolor: '#e57373' } }}>
              {descargandoPdf ? 'Generando...' : 'Descargar'}
            </Button>
          </Box>

          {/* Evoluciones individuales */}
          <Typography sx={{ fontSize: 12, color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
            Evolución individual
          </Typography>
          {evoluciones.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: '#666', fontStyle: 'italic' }}>Sin evoluciones registradas.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 320, overflowY: 'auto' }}>
              {evoluciones.map(ev => {
                const fecha = new Date(ev.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                const preview = (ev.texto || '').replace(/<[^>]*>/g, '').substring(0, 70) + ((ev.texto?.length > 70) ? '...' : '');
                return (
                  <Box key={ev.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px solid var(--c-primario)', borderRadius: 1.5, opacity: ev.anulada ? 0.45 : 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--c-acento)' }}>
                        {fecha}
                        {ev.anulada && <Chip label="ANULADA" size="small" sx={{ ml: 1, bgcolor: '#b71c1c', color: '#fff', fontSize: 10, height: 16 }} />}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#b0b0b0' }}>
                        {ev.cargado_por_nombre || '—'}
                        {ev.diagnostico && ` · ${ev.diagnostico}`}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {preview}
                      </Typography>
                    </Box>
                    <IconButton size="small" disabled={!!ev.anulada || descargandoEvId === ev.id}
                      onClick={() => descargarEvolucionPDF(ev)}
                      sx={{ color: '#ef9a9a', flexShrink: 0 }}
                      title="Descargar esta evolución">
                      {descargandoEvId === ev.id
                        ? <CircularProgress size={16} sx={{ color: '#ef9a9a' }} />
                        : <DownloadIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPdfOpen(false)} sx={{ color: 'var(--c-acento)' }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <DatosPacienteDialog open={verDatosPaciente} onClose={() => setVerDatosPaciente(false)} paciente={paciente} />

      {/* ── Modal Ficha Básica ── */}
      <Dialog open={fichaOpen} onClose={() => setFichaOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: 'var(--c-acento)' }}>
          Ficha Básica — {nombrePaciente}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Antropometría */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Peso (kg)" type="number" value={fichaForm.peso}
              onChange={e => setFichaForm(f => ({ ...f, peso: e.target.value }))}
              InputLabelProps={labelStyle} inputProps={{ ...inputStyle, step: 0.1 }}
              sx={{ flex: 1, ...fieldSx }} />
            <TextField label="Altura (m)" type="number" value={fichaForm.altura}
              onChange={e => setFichaForm(f => ({ ...f, altura: e.target.value }))}
              InputLabelProps={labelStyle} inputProps={{ ...inputStyle, step: 0.01 }}
              sx={{ flex: 1, ...fieldSx }} />
            {imc() && (
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2,
                bgcolor: 'var(--c-primario)', borderRadius: 1, flexShrink: 0 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 10, color: '#90a4ae' }}>IMC</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--c-acento)' }}>{imc()}</Typography>
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ flex: 1, minWidth: 120 }}>
              <InputLabel sx={{ color: '#90a4ae' }}>Grupo sanguíneo</InputLabel>
              <Select value={fichaForm.grupo_sanguineo} label="Grupo sanguíneo"
                onChange={e => setFichaForm(f => ({ ...f, grupo_sanguineo: e.target.value }))}
                sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
                {['', 'A', 'B', 'AB', 'O'].map(g => <MenuItem key={g} value={g}>{g || '—'}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel sx={{ color: '#90a4ae' }}>Factor RH</InputLabel>
              <Select value={fichaForm.factor_rh} label="Factor RH"
                onChange={e => setFichaForm(f => ({ ...f, factor_rh: e.target.value }))}
                sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
                {['', '+', '-'].map(r => <MenuItem key={r} value={r}>{r || '—'}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <TextField label="Alergias" value={fichaForm.alergias} multiline rows={2}
            onChange={e => setFichaForm(f => ({ ...f, alergias: e.target.value }))}
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
          <TextField label="Medicación habitual" value={fichaForm.medicacion_habitual} multiline rows={2}
            onChange={e => setFichaForm(f => ({ ...f, medicacion_habitual: e.target.value }))}
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
          <TextField label="Antecedentes quirúrgicos" value={fichaForm.antecedentes_quirurgicos} multiline rows={2}
            onChange={e => setFichaForm(f => ({ ...f, antecedentes_quirurgicos: e.target.value }))}
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
          <TextField label="Enfermedades crónicas" value={fichaForm.enfermedades_cronicas} multiline rows={2}
            onChange={e => setFichaForm(f => ({ ...f, enfermedades_cronicas: e.target.value }))}
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ flex: 1, minWidth: 120 }}>
              <InputLabel sx={{ color: '#90a4ae' }}>Tabaco</InputLabel>
              <Select value={fichaForm.habito_tabaco} label="Tabaco"
                onChange={e => setFichaForm(f => ({ ...f, habito_tabaco: e.target.value }))}
                sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="ex">Ex fumador</MenuItem>
                <MenuItem value="si">Sí</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel sx={{ color: '#90a4ae' }}>Alcohol</InputLabel>
              <Select value={fichaForm.habito_alcohol} label="Alcohol"
                onChange={e => setFichaForm(f => ({ ...f, habito_alcohol: e.target.value }))}
                sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="ocasional">Ocasional</MenuItem>
                <MenuItem value="frecuente">Frecuente</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField label="Otros datos" value={fichaForm.otros} multiline rows={2}
            onChange={e => setFichaForm(f => ({ ...f, otros: e.target.value }))}
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
          {ficha?.actualizado_por && (
            <Typography sx={{ fontSize: 11, color: '#666' }}>
              Última actualización: {ficha.actualizado_por}
            </Typography>
          )}
          {fichaExito && <Alert severity="success">Ficha guardada correctamente.</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFichaOpen(false)} sx={{ color: 'var(--c-acento)' }}>Cerrar</Button>
          <Button variant="contained" onClick={guardarFicha} disabled={guardandoFicha}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
            {guardandoFicha ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modal Nueva Evolución ── */}
      <Dialog open={evOpen} onClose={() => setEvOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: 'var(--c-acento)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <span>
            Nueva Evolución — {nombrePaciente}
            {evTurnoId && <Typography component="span" sx={{ fontSize: 12, color: '#81c784', ml: 1 }}>· vinculada al turno #{evTurnoId}</Typography>}
          </span>
          <Button size="small" variant="outlined" startIcon={<BadgeIcon sx={{ fontSize: 15 }} />}
            onClick={() => setVerDatosPaciente(true)}
            sx={{ color: '#90a4ae', borderColor: '#546e7a', fontSize: 11, py: 0.3, flexShrink: 0 }}>
            Ver datos del paciente
          </Button>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <TextField label="Evolución *" value={evForm.texto} multiline rows={8}
            onChange={e => setEvForm(f => ({ ...f, texto: e.target.value }))}
            placeholder="Escribí la evolución clínica del paciente..."
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
          <TextField label="Diagnóstico" value={evForm.diagnostico}
            onChange={e => setEvForm(f => ({ ...f, diagnostico: e.target.value }))}
            placeholder="Diagnóstico presuntivo o definitivo..."
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
          <TagInput label="Medicamentos indicados"
            tags={evMedicamentos} onChange={setEvMedicamentos} max={20}
            placeholder="Ej: Ibuprofeno 400mg" />
          <TagInput label="Estudios solicitados"
            tags={evEstudios} onChange={setEvEstudios} max={50}
            placeholder="Ej: Hemograma completo" />
          <TagInput label="Palabras clave"
            tags={evPalabrasClave} onChange={setEvPalabrasClave} max={20}
            placeholder="Ej: HTA, diabetes, control" />
          {evError && <Alert severity="error">{evError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEvOpen(false); setEvTurnoId(null); }} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button variant="contained" onClick={guardarEvolucion} disabled={guardandoEv || verificandoTurno}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
            {verificandoTurno ? 'Verificando turno...' : 'Guardar evolución'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog turno compatible encontrado (±3h) ── */}
      <Dialog open={showTurnoCompatible} onClose={() => setShowTurnoCompatible(false)}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: 'var(--c-acento)' }}>Turno encontrado</DialogTitle>
        <DialogContent>
          <Typography>
            Este paciente tiene un turno hoy a las <strong>{turnoCompatible?.hora_inicio?.slice(0, 5)}</strong> con vos.
          </Typography>
          <Typography sx={{ mt: 1, color: '#90a4ae', fontSize: 13 }}>
            ¿Querés marcar ese turno como atendido al guardar la evolución?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => guardarConTurnoCompatible(true)} disabled={guardandoEv}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
            {guardandoEv ? 'Guardando...' : 'Marcar turno como atendido y guardar'}
          </Button>
          <Button onClick={() => guardarConTurnoCompatible(false)} disabled={guardandoEv}
            sx={{ color: 'var(--c-acento)' }}>
            Guardar sin marcar el turno
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog evolución fuera de turno (sin turno compatible) ── */}
      <Dialog open={showFueraDeTurno} onClose={() => setShowFueraDeTurno(false)}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: '#ffb74d' }}>⚠️ Evolución fuera de turno</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={{ fontSize: 13, color: '#90a4ae' }}>
            No encontramos un turno de este paciente con vos cerca de este horario. Elegí el motivo:
          </Typography>
          <RadioGroup value={motivoFueraTurno} onChange={e => setMotivoFueraTurno(e.target.value)}>
            {MOTIVOS_FUERA_TURNO.map(m => (
              <FormControlLabel key={m.value} value={m.value} control={<Radio sx={{ color: '#607d8b' }} />}
                label={<Typography sx={{ fontSize: 14 }}>{m.label}</Typography>} />
            ))}
          </RadioGroup>
          {motivoFueraTurno === 'otro' && (
            <TextField label="Aclaración" value={motivoFueraTurnoDetalle} multiline rows={2}
              onChange={e => setMotivoFueraTurnoDetalle(e.target.value)}
              InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
          )}
          {evError && <Alert severity="error">{evError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFueraDeTurno(false)} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button variant="contained" onClick={guardarFueraDeTurno} disabled={guardandoEv || !motivoFueraTurno}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
            {guardandoEv ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog confirmación guardar evolución ── */}
      <Dialog open={confirmGuardarEv} onClose={() => setConfirmGuardarEv(false)}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: '#ffb74d' }}>⚠️ Confirmar evolución</DialogTitle>
        <DialogContent>
          <Typography>Este registro <strong>no podrá modificarse</strong> una vez guardado.</Typography>
          <Typography sx={{ color: '#ffb74d', fontWeight: 600, mt: 1 }}>
            ¿Confirmás que los datos son correctos?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGuardarEv(false)} sx={{ color: 'var(--c-acento)' }}>
            Revisar
          </Button>
          <Button onClick={confirmarGuardarEvolucion} variant="contained" disabled={guardandoEv}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
            {guardandoEv ? 'Guardando...' : 'Sí, guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
